import type { JSX } from 'react';
import { useState } from 'react';
import { Label, Select, SelectOption, useToast, Box, IconButton, Icon } from '@wistia/ui';
import type { TimeCodedTranscript } from '~/graphql';
import { trackStructEvent } from '~/utilities/snowplow';
import { styled } from 'styled-components';
import { paths } from '~/singlePageApp/paths';
import { DownloadButton, SelectWrapper, TabWrapper } from './SharedStyledComponents';

type Format = 'srt' | 'txt' | 'vtt';

// Constants
const COPY_TIMEOUT_MS = 3000;

const ButtonContainer = styled(Box)`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

type Props = {
  mediaHashedId: string;
  defaultIetfLanguageTag: string;
  transcripts: TimeCodedTranscript[];
  isAudio: boolean;
};

export const TranscriptTab = ({
  defaultIetfLanguageTag,
  mediaHashedId,
  transcripts,
  isAudio,
}: Props): JSX.Element => {
  const showToast = useToast();
  const [selectedLanguage, setSelectedLanguage] = useState(defaultIetfLanguageTag);
  const [selectedFormat, setSelectedFormat] = useState<Format>('srt');
  const [wasCopied, setWasCopied] = useState(false);

  const changeLanguage = (language: string) => setSelectedLanguage(language);
  const changeFormat = (format: Format) => setSelectedFormat(format);

  const getLanguageName = (languageTag: string): string => {
    const transcript = transcripts.find(
      (transcriptItem) => transcriptItem.ietfLanguageTag === languageTag,
    );
    return transcript?.englishName ?? 'Transcript';
  };

  const formatDisplayName = (format: Format): string => {
    switch (format) {
      case 'srt':
        return 'SRT';
      case 'vtt':
        return 'VTT';
      case 'txt':
        return 'text';
      default:
        return format as string;
    }
  };

  const downloadTranscript = async () => {
    const downloadParams = new URLSearchParams();
    downloadParams.set('disposition', 'attachment');
    downloadParams.set('ietf_language_tag', selectedLanguage);
    const url = paths.transcriptDownload({
      mediaHashedId,
      format: selectedFormat,
      ietfLanguageTag: selectedLanguage,
      disposition: 'attachment',
    });

    try {
      const response = await fetch(url);

      if (response.ok) {
        window.open(response.url, '_blank');

        trackStructEvent({
          category: 'button',
          action: 'click',
          label: `publish-download-transcript${isAudio ? '-audio' : ''}`,
          property: 'language',
          value: selectedLanguage,
        });
      } else {
        throw new Error(response.statusText || 'Error downloading transcript');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      showToast({
        message: 'Error downloading transcript',
        colorScheme: 'error',
      });
    }
  };

  const copyTranscript = async () => {
    const fetchTranscript = async () => {
      const url = paths.transcriptDownload({
        mediaHashedId,
        format: selectedFormat,
        ietfLanguageTag: selectedLanguage,
      });

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch transcript');
      }

      return response.text();
    };

    const handleSuccessfulCopy = () => {
      trackStructEvent({
        category: 'button',
        action: 'click',
        label: 'publish-copy-transcript',
        property: 'language',
        value: selectedLanguage,
      });

      setWasCopied(true);
      setTimeout(() => setWasCopied(false), COPY_TIMEOUT_MS);

      const formatName = formatDisplayName(selectedFormat);
      let message = `Transcript copied to clipboard in ${formatName} format`;

      // Only include the language name if there are multiple languages available
      if (transcripts.length > 1) {
        const languageName = getLanguageName(selectedLanguage);
        message = `${languageName} transcript copied to clipboard in ${formatName} format`;
      }

      showToast({
        message,
        colorScheme: 'success',
      });
    };

    try {
      // Create ClipboardItem with a Promise for Safari compatibility
      const clipboardItem = new ClipboardItem({
        'text/plain': fetchTranscript().then((text) => new Blob([text], { type: 'text/plain' })),
      });
      await navigator.clipboard.write([clipboardItem]);

      handleSuccessfulCopy();
    } catch (_error) {
      showToast({
        message: 'Failed to copy transcript',
        colorScheme: 'error',
      });
    }
  };

  const handleDownload = () => {
    void downloadTranscript();
  };

  return (
    <TabWrapper>
      <SelectWrapper>
        <Label>Language</Label>
        <Select
          onChange={changeLanguage}
          fullWidth={true}
          value={selectedLanguage}
          disabled={transcripts.length === 1}
        >
          {transcripts.map(({ englishName, ietfLanguageTag }) => (
            <SelectOption
              key={ietfLanguageTag}
              value={ietfLanguageTag}
            >
              {englishName}
            </SelectOption>
          ))}
        </Select>
      </SelectWrapper>

      <SelectWrapper>
        <Label>Format</Label>
        <Select
          onChange={changeFormat}
          fullWidth={true}
          value={selectedFormat}
        >
          <SelectOption value="srt">SRT (SubRip subtitle)</SelectOption>
          <SelectOption value="vtt">VTT (Video Text Tracks)</SelectOption>
          <SelectOption value="txt">TXT (Plain text)</SelectOption>
        </Select>
      </SelectWrapper>

      <ButtonContainer
        direction="row"
        gap="space-02"
      >
        <IconButton
          size="lg"
          colorScheme={wasCopied ? 'success' : 'standard'}
          label="Copy transcript to clipboard"
          variant="soft"
          onClick={() => {
            void copyTranscript();
          }}
          data-testid="copy-transcript"
        >
          <Icon type={wasCopied ? 'checkmark' : 'save-as-copy'} />
        </IconButton>

        <DownloadButton
          onClick={handleDownload}
          data-testid="download-transcript"
          style={{ marginLeft: 0 }}
        >
          Download
        </DownloadButton>
      </ButtonContainer>
    </TabWrapper>
  );
};
